import {
  EVENT_TYPE_BIOMETRIC_RESPONSE,
  EVENT_TYPE_BIOMETRIC_AVAILABILITY,
  EVENT_TYPE_LOCATION_PERMISSIONS,
  EVENT_TYPE_LOCATION_DATA,
  EVENT_TYPE_NOTIFICATIONS_PERMISSIONS,
  EVENT_TYPE_NOTIFICATIONS_TOKEN,
  EVENT_TYPE_CAMERA_PERMISSIONS,
  EVENT_TYPE_STORAGE_RESPONSE,
  EVENT_TYPE_BACK_BUTTON,
  EVENT_TYPE_BIOMETRIC_TYPE,
  EVENT_TYPE_LIBRARY_PERMISSIONS,
  EVENT_TYPE_DEVICE_ID,
  EVENT_TYPE_CHECK_NOTIFICATIONS_PERMISSIONS,
  EVENT_TYPE_GENERIC_COMMAND,
  EVENT_TYPE_COPY_TO_CLIPBOARD,
  EVENT_TYPE_CHECK_LOCATION_PERMISSIONS,
  EVENT_TYPE_CHECK_CAMERA_PERMISSIONS,
} from 'utils/events';
import { STORAGE_VAULT } from 'constants/local';
import { LOCAL_STORAGE_PREFIX } from 'constants/global';
import platform from 'utils/platform';
import { scanQrWeb } from 'utils/webQrScanner';
import { sendSentryLog } from './sendSentryLog';

export const OS_ANDROID = 'ANDROID';
export const OS_IOS = 'IOS';
export const OS_LIST = [OS_ANDROID, OS_IOS];

/* eslint-disable */
const urlParams = new URLSearchParams(window.location.search);
const osParam = urlParams.get('os') || window.localStorage.getItem('os') || null;

window.getOS = () => {
  return (osParam || window.localStorage.getItem('os') || '').toUpperCase() || null;
}

window.onIOSApp = () => {
  return osParam && osParam === OS_IOS;
}

window.onAndroidApp = () => {
  return osParam && osParam === OS_ANDROID && typeof window.Android !== 'undefined';
}

window.onNativeApp = () => {
  return osParam && OS_LIST.indexOf(osParam) >= 0;
}

window.getBrowserOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return OS_IOS;
  }

  return OS_ANDROID;
}

/*****************************/
/* NATIVE DEVICE FUNCTIONS */

const userAgent = navigator.userAgent || navigator.vendor || window.opera;

window.onIOSBrowser = () => {
  try {
    return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  } catch (error) {
    return false;
  }
}

window.onAndroidBrowser = () => {
  try {
    return /android/i.test(userAgent);
  } catch (error) {
    return false;
  }
}

window.onNativeBrowser = () => {
  if (window.onIOSBrowser() || window.onAndroidBrowser()) {
    return true;
  }
  return false;
}

/*****************************/

window.onIOS = () => {
  return window.onIOSBrowser() || window.onIOSApp();
}

window.onAndroid = () => {
  return window.onAndroidBrowser() || window.onAndroidApp();
}

window.onApp = () => {
  return window.onAndroidApp() || window.onIOSApp();
}

/*****************************/
/* BIOMETRICS FUNCTIONS */

/**
 * Saves a value to native storage
 * 
 * @param {String} key storage key
 * @param {JSON} value data to save
 */
window.saveValue = (key, value) => {
  try {
    if (window.onAndroidApp()) {
      Android.saveValue(key, value);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.saveValue.postMessage({ key, value });
    } else {
      const vaultKey = `${LOCAL_STORAGE_PREFIX}${STORAGE_VAULT}`;
      let vaultValue = window.localStorage.getItem(vaultKey);
      vaultValue = JSON.parse(vaultValue || '{}') || {};
      vaultValue[key] = value;
      window.localStorage.setItem(vaultKey, JSON.stringify(vaultValue));
    }
  } catch (error) {
    console.error('saveValue error', { error, key, value });
  }
};

/**
 * Retrieves a value from native storage
 * 
 * @param {String} key storage key
 */
window.getValue = (key) => {
  try {
    if (window.onAndroidApp()) {
      Android.getValue(key);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getValue.postMessage(key);
    } else {
      const vaultKey = `${LOCAL_STORAGE_PREFIX}${STORAGE_VAULT}`;
      let vaultValue = window.localStorage.getItem(vaultKey);
      vaultValue = JSON.parse(vaultValue || '{}') || {};
      vaultValue = vaultValue[key] || null;
      if (vaultValue && vaultValue?.startsWith('{')) {
        vaultValue = JSON.parse(vaultValue);
      }
      window.setValue(vaultValue);
    }
  } catch (error) {
    console.error('getValue error', { error, key });
  }
};

/**
 * Success callback from native storage retrieval
 * 
 * @param {String} key storage key
 * @param {JSON} data data retrieved or null if not found
 */
window.setValue = (data) => {
  const event = new CustomEvent(EVENT_TYPE_STORAGE_RESPONSE, { detail: { data } });
  document.dispatchEvent(event);
};

/**
 * Deletes a value from native storage
 * 
 * @param {String} key storage key
 */
window.deleteValue = (key) => {
  try {
    if (window.onAndroidApp()) {
      Android.deleteValue(key);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.deleteValue.postMessage(key);
    } else {
      const vaultKey = `${LOCAL_STORAGE_PREFIX}${STORAGE_VAULT}`;
      let vaultValue = window.localStorage.getItem(vaultKey);
      vaultValue = JSON.parse(vaultValue || '{}') || {};
      delete vaultValue[key];
      window.localStorage.setItem(vaultKey, JSON.stringify(vaultValue));
    }
  } catch (error) {
    console.error('deleteValue error', { error, key });
  }
};

window.canUseBiometrics = () => {
  try {
    if (window.onAndroidApp()) {
      return Android.canAuthenticate();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.canAuthenticate.postMessage('');
    } else {
      window.canAuthenticate(false);
    }
  } catch (error) {
    console.log('canUseBiometrics error', error);
    window.canAuthenticate(false);
  }
};

window.checkBiometricType = () => {
  try {
    if (window.onAndroidApp()) {
      return Android.checkBiometricType();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.checkBiometricType.postMessage('');
    } else {
      window.biometricType('none');
    }
  } catch (error) {
    console.log('checkBiometricType error', error);
    window.biometricType('none');
  }
};

window.biometricType = (response) => {
  const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_TYPE, { detail: response });
  document.dispatchEvent(event);
};

window.showBiometrics = (key) => {
  try {
    if (window.onAndroidApp()) {
      Android.requestAuthentication(key);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.requestAuthentication.postMessage(key);
    }
  } catch (error) {
    console.error('showBiometric error', { error, key });
  }
};


window.canAuthenticate = (response) => {
  const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_AVAILABILITY, { detail: { "biometricsAvailable": response } });
  document.dispatchEvent(event);
};

window.authenticationSucceeded = (data) => {
  try {
    let dataObj = null;
    if (typeof data === 'string') {
      dataObj = JSON.parse(data);
    } else if (typeof data === 'object') {
      dataObj = data;
    }
    if (dataObj) {
      const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_RESPONSE, { detail: { success: true, data: { ...dataObj } } });
      document.dispatchEvent(event);
    } else {
      const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_RESPONSE, { detail: { success: false, data: { error: 'Unable to parse local data' } } });
      document.dispatchEvent(event);
    }
  } catch (error) {
    const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_RESPONSE, { detail: { success: false, data: { error: 'Unable to parse local data' } } });
    document.dispatchEvent(event);
    console.log('authenticationSucceeded error', { error, data });
  }
};

window.authenticationError = (data) => {
  try {
    const dataObj = JSON.parse(data);
    const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_RESPONSE, { detail: { success: false, data: { ...dataObj } } });
    document.dispatchEvent(event);
  } catch (error) {
    const event = new CustomEvent(EVENT_TYPE_BIOMETRIC_RESPONSE, { detail: { success: false, data } });
    document.dispatchEvent(event);
    if (![
      'The user did cancel',
      'Cancelar',
      'El usuario canceló la operación de huella digital.',
      'Se canceló la autenticación',
      'El hardware de huella digital no está disponible.',
      'Se canceló la operación de huella digital.',
    ].includes(data)) {
      sendSentryLog({ data }, 'Biometric error');
    }
  }
};

window.getLocationPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getLocationPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getLocationPermission.postMessage('');
    } else {
      window.setLocationPermission(true);
    }
  } catch (error) {
    console.log('getLocationPermission error', { error });
  }
};

window.setLocationPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_LOCATION_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.checkLocationPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.checkLocationPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.checkLocationPermission.postMessage('');
    } else {
      navigator.permissions.query({ name: 'geolocation' })
        .then(function(permissionStatus) {
          window.hasLocationPermission(permissionStatus?.state === 'granted');
        });
    }
  } catch (error) {
    console.log('checkLocationPermission error', { error });
    window.hasLocationPermission(false);
  }
};

window.hasLocationPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_CHECK_LOCATION_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.getCurrentPosition = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getCurrentPosition();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getCurrentPosition.postMessage('');
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          window.setCurrentPosition(position.coords.latitude, position.coords.longitude);
        },
        (err) => {
          console.error('getCurrentPosition error', err);
        }
      );
    }
  } catch (error) {
    console.log('getCurrentPosition error', { error });
  }
};

window.setCurrentPosition = (latitude, longitude) => {
  const event = new CustomEvent(EVENT_TYPE_LOCATION_DATA, { detail: { latitude, longitude } });
  document.dispatchEvent(event);
};

window.getNotificationPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getNotificationPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getNotificationPermission.postMessage('');
    } else {
      window.setNotificationPermission(true);
    }
  } catch (error) {
    window.setNotificationPermission(false);
  }
};

window.setNotificationPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_NOTIFICATIONS_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.checkNotificationPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.checkNotificationPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.checkNotificationPermission.postMessage('');
    } else {
      window.hasNotificationPermission(-1);
    }
  } catch (error) {
    window.hasNotificationPermission(-1);
  }
};

window.hasNotificationPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_CHECK_NOTIFICATIONS_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.getFCMToken = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getFCMToken();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getFCMToken.postMessage('');
    } else {
      window.setFCMToken('');
    }
  } catch (error) {
    console.log('getFCMToken error', { error });
  }
};

window.setFCMToken = (token) => {
  const event = new CustomEvent(EVENT_TYPE_NOTIFICATIONS_TOKEN, { detail: { token } });
  document.dispatchEvent(event);
};

window.getCameraPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getCameraPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getCameraPermission.postMessage('');
    } else {
      window.setCameraPermission(true);
    }
  } catch (error) {
    console.log('getCameraPermission error', { error });
  }
};

window.setCameraPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_CAMERA_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.checkCameraPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.checkCameraPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.checkCameraPermission.postMessage('');
    } else {
      navigator.permissions.query({ name: 'camera' })
        .then(function(permissionStatus) {
          window.hasCameraPermission(permissionStatus?.state === 'granted');
        });
    }
  } catch (error) {
    console.log('checkCameraPermission error', { error });
    window.hasCameraPermission(false);
  }
};

window.hasCameraPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_CHECK_CAMERA_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.openFinsus = () => {
  try {
    if (window.onAndroidApp()) {
      Android.openFinsus();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.openFinsus.postMessage('');
    }
  } catch (error) {
    console.log('openFinsus error', { error });
  }
};

window.backPressed = () => {
  const event = new CustomEvent(EVENT_TYPE_BACK_BUTTON);
  document.dispatchEvent(event);
};

window.downloadDocument = (url, filename) => {
  try {
    if (window.onAndroidApp()) {
      Android.downloadDocument(url, filename);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.downloadDocument.postMessage({ url, filename });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    console.log('downloadDocument error', { error, url, filename });
  }
};

/**
 * Saves a file from base64 (e.g. KLU wallet postMessage DOWNLOAD_FILE) to native Downloads / Documents.
 * @param {string} base64 - Raw base64 (no data: prefix)
 * @param {string} mimeType - e.g. application/pdf
 * @param {string} fileName - suggested file name with extension
 */
window.saveBlobFile = (base64, mimeType, fileName) => {
  try {
    if (window.onAndroidApp()) {
      Android.saveBlobFile(base64, mimeType, fileName);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.saveBlobFile.postMessage({ base64, mimeType, fileName });
    } else {
      const bytes = atob(base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) {
        arr[i] = bytes.charCodeAt(i);
      }
      const blob = new Blob([arr], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      // Safari iOS no soporta <a download> — usar window.open
      if (platform.isIOS && !platform.isNativeApp) {
        window.open(blobUrl);
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    }
  } catch (error) {
    console.error('saveBlobFile error', { error, mimeType, fileName });
  }
};

/** Called from native when user cancels the save-confirm dialog (Android/iOS). */
window.downloadCancelled = () => {
  console.debug('[native] download cancelled by user');
};

function blobToBase64(blob, callback) {
  const reader = new FileReader();
  reader.onload = function () {
    const dataUrl = reader.result;
    const base64 = dataUrl.split(',')[1];
    callback(base64);
  };
  reader.readAsDataURL(blob);
}

window.share = (title, message, type, isBase64 = false) => {
  try {
    if (window.onAndroidApp()) {
      if (isBase64) {
        Android.share(title, message, type);
      } else {
        blobToBase64(message, (base64) => {
          Android.share(title, base64, type);
        });
      }
    } else if (window.onIOSApp()) {
      if (isBase64) {
        window.webkit.messageHandlers.share.postMessage({ title, message, type });
      } else {
        blobToBase64(message, (base64) => {
          window.webkit.messageHandlers.share.postMessage({ title, message: base64, type });
        });
      }
    } else {
      if (isBase64) {
        // Intentar Web Share API si está disponible (navegadores móviles modernos)
        if (typeof navigator.share === 'function') {
          navigator.share({ title, text: message, url: message }).catch(() => {
            const fakeLink = document.createElement('a');
            fakeLink.href = message;
            fakeLink.download = title;
            document.body.appendChild(fakeLink);
            fakeLink.click();
            document.body.removeChild(fakeLink);
          });
        } else {
          const fakeLink = document.createElement('a');
          fakeLink.href = message;
          fakeLink.download = title;
          document.body.appendChild(fakeLink);
          fakeLink.click();
          document.body.removeChild(fakeLink);
        }
      } else {
        const fileURL = URL.createObjectURL(message);
        if (typeof navigator.share === 'function') {
          navigator.share({ title, text: title, url: fileURL }).catch(() => {
            window.open(fileURL);
          });
        } else {
          window.open(fileURL);
        }
      }
    }
  } catch (error) {
    console.log('share error', { error, title, message, type });
  }
}

window.onShare = (data) => {
  console.log('onShare', data);
}

window.getPhotoLibraryPermission = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getPhotoLibraryPermission();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getPhotoLibraryPermission.postMessage('');
    } else {
      window.setPhotoLibraryPermission(true);
    }
  } catch (error) {
    console.log('getPhotoLibraryPermission error', { error });
  }
};

window.setPhotoLibraryPermission = (permission) => {
  const event = new CustomEvent(EVENT_TYPE_LIBRARY_PERMISSIONS, { detail: { permission } });
  document.dispatchEvent(event);
};

window.getUIdToken = () => {
  try {
    if (window.onAndroidApp()) {
      Android.getUIdToken();
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.getUIdToken.postMessage('');
    } else {
      // Browser: no native bridge — resolve with empty id via the real dispatcher
      // (window.setDeviceId does not exist; the dispatcher is window.setUIdToken).
      window.setUIdToken('');
    }
  } catch (error) {
    console.log('getDeviceId error', { error });
  }
};

window.setUIdToken = (id) => {
  const event = new CustomEvent(EVENT_TYPE_DEVICE_ID, { detail: id });
  document.dispatchEvent(event);
};

window.openExternalLink = (url) => {
  try {
    if (window.onAndroidApp()) {
      Android.openExternalLink(url);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.openExternalLink.postMessage({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    window.open(url, '_blank', 'noopener,noreferrer');
    console.log('openExternalLink error', { error, url });
  }
};

window.openDobleAuth = (url) => {
  try {
    if (window.onAndroidApp()) {
      Android.openDobleAuth(url);
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.openDobleAuth.postMessage({ url });
    } else {
      window.open(url, '_blank');
    }
  } catch (error) {
    window.open(url, '_blank');
    console.log('openDobleAuth error', { error, url });
  }
};

window.copyToClipboard = (text) => {
  try {    
    if (window.onAndroidApp()) {
      Android.copyToClipboard(text)
    } else if (window.onIOSApp()) {
      window.webkit.messageHandlers.copyToClipboard.postMessage(text)
    } else {
      navigator.clipboard.writeText(text);
      window.copySuccess(true);
    }
  } catch (error) {
    console.log('copyToClipboard error', { error, text });
  }
};

window.copySuccess = (success) => {
  const event = new CustomEvent(EVENT_TYPE_COPY_TO_CLIPBOARD, { detail: { success } });
  document.dispatchEvent(event);
};

window.genericCommand = (type, payload) => {
  console.debug('window.genericCommand', JSON.stringify({ type, payload }));
  try {
    if (platform.isAndroid) {
      Android.genericCommandRequest(JSON.stringify({ type, payload }));
    } else if (platform.isIOS) {
      window.webkit.messageHandlers.genericCommandRequest.postMessage({ type, payload });
    } else if (type === 'READ_QR') {
      // Fallback web: usar escáner QR basado en cámara del navegador
      scanQrWeb()
        .then((result) => {
          window.genericCommandResponse(JSON.stringify(result));
        })
        .catch((err) => {
          console.error('scanQrWeb error', err);
          window.genericCommandResponse(JSON.stringify({ result: null, error: err.message }));
        });
    }
  } catch (error) {
    console.error('genericCommand error', { type, payload, error });
  }
};

window.genericCommandResponse = (response) => {
  console.debug('window.genericCommandResponse', response);
  sendSentryLog({ response }, 'genericCommandResponse');
  try {
    let responseData = typeof response === 'string' ? JSON.parse(response) : response || {};
    const event = new CustomEvent(EVENT_TYPE_GENERIC_COMMAND, { detail: responseData });
    document.dispatchEvent(event);
  } catch (error) {
    sendSentryLog({ error, response }, 'genericCommandResponse: error');
    console.error('genericCommandResponse error', { error, response });
  }
};